import { Component, computed, inject, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { FieldService } from '@core/jss-utils';
import { RichTextWrapperComponent } from '@legacy/common-ui-utility-components';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { CoreJssProxyModule } from '@core/jss-proxy';

import { HeroFieldService } from '../services/hero-field.service';
import { HeroWrapperComponent } from '../hero-wrapper/hero-wrapper.component';
import { HeroHeadingComponent } from '../hero-heading/hero-heading.component';
import { HeroButtonGroupComponent } from '../hero-button-group/hero-button-group.component';
import { HeroWidgetComponent } from '../hero-widget/hero-widget.component';
import { HeroCardWrapperComponent } from '../hero-card-wrapper/hero-card-wrapper.component';
import { type Hero } from '../models/hero.model';

@Component({
  selector: 'wl-hero',
  standalone: true,
  imports: [
    HeroWrapperComponent,
    HeroHeadingComponent,
    HeroButtonGroupComponent,
    HeroWidgetComponent,
    HeroCardWrapperComponent,
    RichTextWrapperComponent,
    CoreJssProxyModule,
    NgClass,
  ],
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.ed.scss', './hero.component.essent.scss'],
})
export class HeroComponent {
  readonly #brand = inject(ENVIRONMENT_CONFIG).brand;
  readonly #fieldService = inject(FieldService);
  readonly #heroFieldService = inject(HeroFieldService);

  readonly id = input<string>();
  readonly hero = input.required<Hero>();
  readonly hasWidget = input<boolean>(false);
  readonly hasAsset = input<boolean>(false);
  readonly hasBreadcrumbs = input<boolean>(false);
  readonly hasEssentRebranding = input<boolean>(false);

  readonly isEssent = computed(() => this.#brand === 'essent');

  readonly paragraph = computed(() => this.hero()?.paragraph);
  readonly richText = computed(() => this.hero()?.richText);
  readonly bottomImage = computed(() => this.hero()?.bottomImage);
  readonly bottomImageMobile = computed(() => this.hero()?.bottomImageMobile);
  readonly useBottomImageOnMobile = computed(
    () => this.hero()?.useBottomImageOnMobile
  );
  readonly mainImage = computed(() => this.hero()?.mainImage);

  readonly showParagraph = computed(() =>
    this.#fieldService.showTextField(this.paragraph())
  );
  readonly showRichText = computed(() =>
    this.#fieldService.showTextField(this.richText())
  );
  readonly showMainImage = computed(() =>
    this.#fieldService.showImageField(this.mainImage())
  );
  readonly showBottomImage = computed(() =>
    this.#fieldService.showImageField(this.bottomImage())
  );
  readonly showBottomImageMobile = computed(() =>
    this.#fieldService.showImageField(this.bottomImageMobile())
  );

  readonly showButtonGroup = computed(() =>
    this.#heroFieldService.showButtonGroup(this.hero())
  );
  readonly hasMultiColumnLayout = computed(() =>
    this.#heroFieldService.hasMultiColumnLayout(this.hero(), this.hasWidget())
  );

  readonly hasCard = computed(
    () =>
      this.hasEssentRebranding() &&
      this.#heroFieldService.showCard(
        this.hero(),
        this.hasAsset(),
        this.isEssent()
      )
  );

  readonly bottomImageClass = computed(() => {
    const showMobile = this.showBottomImageMobile();
    const useOnMobile = this.useBottomImageOnMobile();

    return showMobile || !useOnMobile ? 'd-none d-md-block' : undefined;
  });
}
